import type { Props } from 'tippy.js';

import { environment } from 'src/environments/environment';

const isV1Collection = environment.collection === 'cryptopunks-v1';

export const PUNKSMARKET_APP_URL = 'https://punksmarket.app/';

/** Rich HTML tooltip for V1 on-chain bid labels (Tippy allowHTML). */
export const onChainBidContractTooltipContent =
  'Using <a href="https://punksmarket.app/" target="_blank" rel="noopener noreferrer">punksmarket.app</a> contract';

export const showOnChainBidContractTooltip = isV1Collection;

export const onChainBidContractTippyOptions: Partial<Props> | undefined =
  isV1Collection
    ? {
        content: onChainBidContractTooltipContent,
        placement: 'top',
        theme: 'punks-onchain-bid',
      }
    : undefined;

/** Lowercase noun phrase: "wrapped bid" on V1, "legacy bid" on V2. */
export const nativeBidNoun = isV1Collection ? 'wrapped bid' : 'legacy bid';

/** Plural noun phrase: "wrapped bids" on V1, "legacy bids" on V2. */
export const nativeBidNounPlural = isV1Collection ? 'wrapped bids' : 'legacy bids';

/** Sentence-case plural for notification settings. */
export const nativeBidNotificationsLabel =
  nativeBidNounPlural.charAt(0).toUpperCase() + nativeBidNounPlural.slice(1);

/** Title-case bid type label for buttons and tables. */
export const nativeBidTypeLabel = isV1Collection ? 'Wrapped Bid' : 'Legacy Bid';

/** Plural title-case label for section headings. */
export const nativeBidTypeLabelPlural = isV1Collection ? 'Wrapped Bids' : 'Legacy Bids';

/** Marketplace source label in bid lists. */
export const nativeBidMarketplaceSource = isV1Collection
  ? 'Wrapped Marketplace'
  : 'Legacy Marketplace';

/** Lowercase noun: "on-chain bid" on V1, "PunksMarket bid" on V2. */
export const onChainBidNoun = isV1Collection ? 'on-chain bid' : 'PunksMarket bid';

/** Plural noun for lists and tabs. */
export const onChainBidNounPlural = isV1Collection ? 'on-chain bids' : 'PunksMarket bids';

/** Pill / fallback type label. */
export const onChainBidTypeLabel = isV1Collection ? 'On-chain bid' : 'PunksMarket bid';

/** Title-case pill label in tables. */
export const onChainBidTypeLabelTitle = isV1Collection ? 'On-chain Bid' : 'PunksMarket Bid';

export const placeOnChainBidButtonLabel = isV1Collection
  ? 'Place on-chain bid'
  : 'Place PunksMarket bid';

export const acceptOnChainBidButtonLabel = isV1Collection
  ? 'Accept on-chain bid'
  : 'Accept PunksMarket bid';

export const acceptOnChainBidModalTitle = isV1Collection
  ? 'Accept on-chain bid'
  : 'Accept PunksMarket bid';

/** Seller confirmation copy before router acceptBid. */
export const acceptOnChainBidModalDescription = isV1Collection
  ? 'Your unwrapped punk must be listed for sale to the PunksMarket router (directed listing). The bidder’s escrow pays you the bid amount in ETH when you confirm.'
  : 'Your punk must be listed for sale to PunksMarket. The bidder’s escrow pays you when you confirm.';

export const acceptOnChainBidListingRequiredTitle = isV1Collection
  ? 'List on PunksMarket first'
  : 'List on PunksMarket first';

export const acceptOnChainBidListingRequiredDescription = isV1Collection
  ? 'To accept an on-chain bid, list this unwrapped punk for sale to punksmarket.eth (directed listing). You can delist again after the sale if needed.'
  : 'To accept a PunksMarket bid, list this punk for sale to the PunksMarket router first.';

/** Shown on the sell modal when listing price is prefilled before acceptBid. */
export const acceptOnChainBidListingPriceHint = isV1Collection
  ? 'Listing price is prefilled to match the on-chain bid you are accepting. PunksMarket settles against this directed listing.'
  : 'Listing price is prefilled to match the PunksMarket bid you are accepting.';

/** Criteria prefix: "On-chain · #123" vs "PunksMarket · #123". */
export const onChainBidCriteriaPrefix = isV1Collection ? 'On-chain' : 'PunksMarket';

export const onChainBidPlatformTooltip = isV1Collection
  ? 'On-chain bid'
  : 'PunksMarket (on-chain)';

export const onChainBidMarketTabLabel = isV1Collection ? 'On-chain bids' : 'PunksMarket bids';

export const onChainBidSectionTitle = isV1Collection ? 'On-chain Bids' : 'PunksMarket Bids';

export const onChainBidLoadingMessage = isV1Collection
  ? 'Loading on-chain bids…'
  : 'Loading PunksMarket bids…';

export const onChainBidLoadError = isV1Collection
  ? 'Failed to load on-chain bids.'
  : 'Failed to load PunksMarket bids.';

export const onChainBidDetailsLoadError = isV1Collection
  ? 'Failed to load on-chain bid details'
  : 'Failed to load PunksMarket bid details';

export const onChainBidCancelSuccess = isV1Collection
  ? 'Your on-chain bid has been canceled.'
  : 'Your PunksMarket bid has been canceled on-chain.';

export const onChainBidCancelFailure = isV1Collection
  ? 'Unable to cancel this on-chain bid. Check your wallet and try again.'
  : 'Unable to cancel this PunksMarket bid. Check your wallet and try again.';

export const onChainBidAcceptFailure = isV1Collection
  ? 'Failed to accept on-chain bid'
  : 'Failed to accept PunksMarket bid';

export const onChainBidOutgoingSectionTitle = isV1Collection
  ? 'Outgoing On-chain Bids for this Account'
  : 'Outgoing PunksMarket Bids for this Account';

export const onChainBidEscrowHint = isV1Collection
  ? 'Funds are locked in the on-chain escrow contract until you cancel the bid or it is accepted against a directed listing.'
  : 'Funds are locked in the PunksMarket contract until you cancel the bid or it is accepted against a directed listing.';

/** Clone action — stash and on-chain bids. */
export const cloneBidLabel = 'Clone bid';

export const cloneBidConfirmLabel = 'Confirm & Clone bid';
